import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  monitoringAbsenteesService,
  mapMonitoringAbsenteesPage,
  type LC_ListMonitoringAbsenteesIn,
  type LC_MonitoringFiltersIn,
  type MonitoringAbsentee,
} from '@/api/domains/attendance/monitoring';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
  PaginatedListSort,
} from '@/hooks/ui/usePaginatedList';
import { useUI } from '@/hooks/ui/useUI';


export interface UseMonitoringAbsenteesResult {
  rows: MonitoringAbsentee[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  loading: boolean;
  /**
   * `true` desde que el backend responde por primera vez. La pantalla siempre arranca con una
   * consulta (sin filtros explícitos el backend usa el holding de la empresa del token), así que
   * antes de esa primera respuesta "no hay datos" nunca es cierto: sirve para no mostrar el estado
   * vacío en el frame inicial. No se resetea al cambiar de filtros: ahí ya hay filas visibles.
   */
  hasLoadedOnce: boolean;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  refetch: () => void;
}

const DEFAULT_SORT_KEY = 'fullName';

// El mismo orden por defecto en las dos capitalizaciones que usa el stack: Table emite direcciones
// en minúscula y el orderDirection del backend las espera en mayúscula. La versión en minúscula se
// comparte con MonitoringAbsenteesTable para que la flecha del encabezado y el orden real no se
// desalineen.
export const MONITORING_ABSENTEES_DEFAULT_SORT = {
  key: DEFAULT_SORT_KEY,
  direction: 'asc',
} as const;
const INITIAL_SORT: PaginatedListSort = { key: DEFAULT_SORT_KEY, direction: 'ASC' };

// La columna `rut` de la tabla se mapea al campo de la whitelist de orderBy del backend
// (MonitoringAbsenteesOrderByFields.EMPLOYEE_TAX_ID). Si se omite, el validador del backend rechaza
// la petición. `fullName` ya coincide en ambos lados; `status` no es ordenable (su etiqueta es
// constante, así que no está en la whitelist del backend ni se expone como columna ordenable).
const ORDER_BY_MAP: Record<string, string> = {
  rut: 'employeeTaxIdentificationNumber',
};

/**
 * Listado paginado "Ausentes" de la pantalla de Monitoreo de Asistencia. Búsqueda, orden y
 * paginación se resuelven server-side vía `usePaginatedList`: el conjunto de ausentes no tiene
 * techo (su definición incluye a quienes ya marcaron salida, así que al final del día se acerca a
 * toda la población activa del holding) y no se puede mantener en memoria.
 *
 * El fetch queda deshabilitado hasta que el panel de filtros compartido emite su primer payload, y
 * cada cambio posterior de `appliedFilters` resetea la paginación y vuelve a consultar.
 */
export function useMonitoringAbsentees(
  appliedFilters: LC_MonitoringFiltersIn | null,
  refreshToken = 0,
): UseMonitoringAbsenteesResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<LC_MonitoringFiltersIn | null>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<MonitoringAbsentee> | null> => {
      // Guarda funcional, no defensiva: en el render donde appliedFilters pasa de null a objeto,
      // `enabled` ya es true pero el filters interno de usePaginatedList todavía es null (este
      // useEffect corre después de su effect de fetch). Sin esta guarda se consultaría el endpoint
      // sin filtros, o sea el pipeline completo sobre todo el holding. No marca hasLoadedOnce:
      // no hubo respuesta del backend.
      if (params.filters === null) {
        return { items: [], totalRecords: 0 };
      }

      const request: LC_ListMonitoringAbsenteesIn = {
        ...params.filters,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText ?? null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      };

      const response = await monitoringAbsenteesService.get(request, signal);

      if (!response.success || !response.data) {
        // Un error también cuenta como primera respuesta: ya no corresponde el skeleton.
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('attendance:monitoring.absentees.loadError'));
          setHasLoadedOnce(true);
        }
        return null;
      }

      setHasLoadedOnce(true);
      return mapMonitoringAbsenteesPage(response.data);
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    setFilters,
    refetch,
  } = usePaginatedList<MonitoringAbsentee, LC_MonitoringFiltersIn | null>({
    fetchFn,
    initialFilters: appliedFilters,
    initialSort: INITIAL_SORT,
    orderByMap: ORDER_BY_MAP,
    enabled: appliedFilters !== null,
  });

  useEffect(() => {
    setFilters(appliedFilters);
  }, [appliedFilters, setFilters]);

  // Reconsulta cuando cambia el token de refresco de la pantalla, conservando búsqueda, orden y
  // paginación; el token inicial no dispara un fetch extra.
  const previousRefreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (previousRefreshTokenRef.current === refreshToken) return;
    previousRefreshTokenRef.current = refreshToken;
    refetch();
  }, [refreshToken, refetch]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    hasLoadedOnce,
    onSearch,
    onSortChange,
    refetch,
  };
}

export default useMonitoringAbsentees;
